import { formatDateTime } from '@erp/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { useId, useRef } from 'react';
import { useNavigate } from 'react-router';

import { api, errorMessage } from '../../lib/api.ts';
import { cn } from '../../lib/cn.ts';
import {
  NOTIFICATIONS_KEY,
  useNotifications,
  type NotificationItem,
} from '../../lib/notifications.ts';
import { useToast } from '../../lib/toast.tsx';

/** In-app notices (mentions, assignments, reminders) on the native `popover`. */
export function NotificationBell({ timeZone }: { timeZone: string }) {
  const id = useId();
  const panel = useRef<HTMLDivElement>(null);
  const { data } = useNotifications();
  const client = useQueryClient();
  const navigate = useNavigate();
  const notify = useToast();
  const unread = data?.unread ?? 0;
  const refresh = () => client.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });

  const open = async (item: NotificationItem) => {
    panel.current?.hidePopover();
    try {
      if (!item.read) await api(`/notifications/${item.id}/read`, { method: 'POST' });
      await refresh();
    } catch (error) {
      notify(errorMessage(error), 'error');
    }
    if (item.link) await navigate(item.link);
  };

  const readAll = async () => {
    try {
      await api('/notifications/read-all', { method: 'POST' });
      await refresh();
    } catch (error) {
      notify(errorMessage(error), 'error');
    }
  };

  return (
    <>
      <button
        type="button"
        popoverTarget={id}
        aria-label={unread > 0 ? `Avisos (${String(unread)} sin leer)` : 'Avisos'}
        className="relative grid size-11 place-items-center rounded-lg hover:bg-muted"
      >
        <Bell className="size-5" aria-hidden="true" />
        {unread > 0 && (
          <span
            aria-hidden="true"
            className="absolute top-1.5 right-1.5 grid min-w-5 place-items-center rounded-full bg-danger px-1 text-[11px] leading-5 font-semibold text-danger-foreground"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      <div
        ref={panel}
        id={id}
        popover="auto"
        className="fixed inset-auto top-14 right-3 m-0 max-h-[70dvh] w-[min(24rem,calc(100vw-1.5rem))] overflow-y-auto rounded-xl border border-border bg-surface p-2 text-foreground shadow-lg"
      >
        <div className="flex items-center justify-between px-3 pt-2 pb-2">
          <h2 className="font-semibold">Avisos</h2>
          {unread > 0 && (
            <button
              type="button"
              onClick={() => void readAll()}
              className="min-h-9 rounded-md px-2 text-sm text-primary hover:bg-muted"
            >
              Marcar todo como leído
            </button>
          )}
        </div>
        {!data || data.items.length === 0 ? (
          <p className="px-3 pb-3 text-sm text-muted-foreground">No tienes avisos.</p>
        ) : (
          <ul className="space-y-0.5">
            {data.items.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => void open(item)}
                  className={cn(
                    'flex w-full gap-3 rounded-lg px-3 py-2 text-left hover:bg-muted',
                    !item.read && 'bg-soft/60',
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      'mt-2 size-2 shrink-0 rounded-full',
                      item.read ? 'bg-transparent' : 'bg-primary',
                    )}
                  />
                  <span className="min-w-0 flex-1">
                    <span className={cn('block text-sm', !item.read && 'font-semibold')}>
                      {item.title}
                      {!item.read && <span className="sr-only"> (sin leer)</span>}
                    </span>
                    {item.body && (
                      <span className="block truncate text-sm text-muted-foreground">
                        {/* The first line (a lead's name and phone); the email has it all. */}
                        {item.body.split('\n')[0]}
                      </span>
                    )}
                    <span className="block text-xs text-muted-foreground">
                      {formatDateTime(item.createdAt, timeZone)}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
